import { supabase, TABLES } from '../config/supabase';

/**
 * Standardized error object structure
 * @typedef {Object} ServiceError
 * @property {boolean} success - Always false for errors
 * @property {string} message - User-friendly error message
 * @property {string} code - Error code for programmatic handling
 * @property {*} details - Additional error details
 */

/**
 * Standardized success response structure
 * @typedef {Object} ServiceResponse
 * @property {boolean} success - Always true for success
 * @property {*} data - Response data
 * @property {string} [message] - Optional success message
 */

/**
 * Creates a standardized error object
 * @param {string} message - User-friendly error message
 * @param {string} code - Error code
 * @param {*} details - Additional error details
 * @returns {ServiceError}
 */
const createError = (message, code = 'UNKNOWN_ERROR', details = null) => ({
  success: false,
  message,
  code,
  details
});

/**
 * Creates a standardized success response
 * @param {*} data - Response data
 * @param {string} [message] - Optional success message
 * @returns {ServiceResponse}
 */
const createSuccess = (data, message = null) => ({
  success: true,
  data,
  ...(message && { message })
});

/**
 * Parses Supabase errors into standardized error objects
 * @param {*} error - Supabase error object
 * @param {string} operation - Operation being performed
 * @returns {ServiceError}
 */
const parseSupabaseError = (error, operation) => {
  if (!error) {
    return createError(
      `Unknown error during ${operation}`,
      'UNKNOWN_ERROR'
    );
  }

  // Handle specific Supabase error codes
  const errorMap = {
    'PGRST116': {
      message: 'Record not found',
      code: 'NOT_FOUND'
    },
    '23505': {
      message: 'A record with this information already exists',
      code: 'DUPLICATE'
    },
    '23503': {
      message: 'Related record not found',
      code: 'FOREIGN_KEY_VIOLATION'
    },
    '42501': {
      message: 'Permission denied',
      code: 'PERMISSION_DENIED'
    }
  };

  const mappedError = errorMap[error.code];
  
  if (mappedError) {
    return createError(
      mappedError.message,
      mappedError.code,
      { originalError: error }
    );
  }

  // Default error response
  return createError(
    error.message || `Failed to ${operation}`,
    error.code || 'DATABASE_ERROR',
    { originalError: error }
  );
};

class ContactsService {
  constructor() {
    this.tableName = TABLES.CONTACTS;
  }

  /**
   * Fetches all contacts with optional filters
   * @param {string} select - Fields to select
   * @param {Object} filters - Filter conditions
   * @returns {Promise<ServiceResponse|ServiceError>}
   */
  async getAll(select = '*', filters = {}) {
    try {
      let query = supabase
        .from(this.tableName)
        .select(select)
        .is('deleted_at', null);

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        query = query.eq(key, value);
      });

      const { data, error } = await query;

      if (error) {
        return parseSupabaseError(error, 'fetch contacts');
      }

      return createSuccess(data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return createError(
        'An unexpected error occurred while fetching contacts',
        'UNEXPECTED_ERROR',
        { error }
      );
    }
  }

  /**
   * Fetches contacts with their associated account information
   * @param {Object} filters - Optional filter conditions
   * @returns {Promise<ServiceResponse|ServiceError>}
   */
  async getContactsWithAccount(filters = {}) {
    try {
      let query = supabase
        .from(this.tableName)
        .select(`
          *,
          accounts (
            id,
            name,
            industry,
            city,
            country
          )
        `)
        .is('deleted_at', null);

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        query = query.eq(key, value);
      });

      const { data, error } = await query;

      if (error) {
        return parseSupabaseError(error, 'fetch contacts with accounts');
      }

      return createSuccess(data || []);
    } catch (error) {
      console.error('Error fetching contacts with accounts:', error);
      return createError(
        'An unexpected error occurred while fetching contacts with accounts',
        'UNEXPECTED_ERROR',
        { error }
      );
    }
  }

  /**
   * Fetches contacts by account ID
   * RLS policies handle data security at the database level
   * @param {string} accountId - Account ID to filter by
   * @returns {Promise<ServiceResponse|ServiceError>}
   */
  async getContactsByAccount(accountId) {
    try {
      if (!accountId) {
        return createError(
          'Account ID is required',
          'INVALID_INPUT'
        );
      }

      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('account_id', accountId)
        .is('deleted_at', null);

      if (error) {
        return parseSupabaseError(error, 'fetch contacts by account');
      }

      // RLS policies ensure users only see contacts they're authorized to view
      // No client-side filtering needed - trust the database security
      return createSuccess(data || []);
    } catch (error) {
      console.error('Error fetching contacts by account:', error);
      return createError(
        'An unexpected error occurred while fetching contacts by account',
        'UNEXPECTED_ERROR',
        { error }
      );
    }
  }

  /**
   * Fetches a single contact by ID
   * @param {string} id - Contact ID
   * @param {string} select - Fields to select
   * @returns {Promise<ServiceResponse|ServiceError>}
   */
  async getById(id, select = '*') {
    try {
      if (!id) {
        return createError(
          'Contact ID is required',
          'INVALID_INPUT'
        );
      }

      const { data, error } = await supabase
        .from(this.tableName)
        .select(select)
        .eq('id', id)
        .single();

      if (error) {
        return parseSupabaseError(error, 'fetch contact');
      }

      return createSuccess(data);
    } catch (error) {
      console.error('Error fetching contact by ID:', error);
      return createError(
        'An unexpected error occurred while fetching contact',
        'UNEXPECTED_ERROR',
        { error }
      );
    }
  }

  /**
   * Creates a new contact
   * @param {Object} data - Contact data
   * @returns {Promise<ServiceResponse|ServiceError>}
   */
  async create(data) {
    try {
      if (!data) {
        return createError(
          'Contact data is required',
          'INVALID_INPUT'
        );
      }

      // Validate required fields
      if (!data.first_name || !data.last_name || !data.account_id) {
        return createError(
          'First name, last name, and account ID are required',
          'INVALID_INPUT'
        );
      }

      // Add the current user as creator
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return createError(
          'User not authenticated',
          'UNAUTHORIZED'
        );
      }

      const contactData = {
        ...data,
        created_by_user_id: user.id
      };

      const { data: result, error } = await supabase
        .from(this.tableName)
        .insert(contactData)
        .select()
        .single();

      if (error) {
        return parseSupabaseError(error, 'create contact');
      }

      return createSuccess(result, 'Contact created successfully');
    } catch (error) {
      console.error('Error creating contact:', error);
      return createError(
        'An unexpected error occurred while creating contact',
        'UNEXPECTED_ERROR',
        { error }
      );
    }
  }

  /**
   * Updates an existing contact
   * @param {string} id - Contact ID
   * @param {Object} data - Updated contact data
   * @returns {Promise<ServiceResponse|ServiceError>}
   */
  async update(id, data) {
    try {
      if (!id) {
        return createError(
          'Contact ID is required',
          'INVALID_INPUT'
        );
      }

      if (!data || Object.keys(data).length === 0) {
        return createError(
          'Update data is required',
          'INVALID_INPUT'
        );
      }

      const { data: result, error } = await supabase
        .from(this.tableName)
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return parseSupabaseError(error, 'update contact');
      }

      return createSuccess(result, 'Contact updated successfully');
    } catch (error) {
      console.error('Error updating contact:', error);
      return createError(
        'An unexpected error occurred while updating contact',
        'UNEXPECTED_ERROR',
        { error }
      );
    }
  }

  /**
   * Deletes a contact
   * @param {string} id - Contact ID
   * @returns {Promise<ServiceResponse|ServiceError>}
   */
  async delete(id) {
    try {
      if (!id) {
        return createError(
          'Contact ID is required',
          'INVALID_INPUT'
        );
      }

      const { error } = await supabase
        .from(this.tableName)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        return parseSupabaseError(error, 'delete contact');
      }

      return createSuccess(null, 'Contact deleted successfully');
    } catch (error) {
      console.error('Error deleting contact:', error);
      return createError(
        'An unexpected error occurred while deleting contact',
        'UNEXPECTED_ERROR',
        { error }
      );
    }
  }

  /**
   * Checks if user can view unmasked contact information
   * @param {string} contactId - Contact ID
   * @returns {Promise<boolean>}
   */
  async canViewUnmasked(contactId) {
    try {
      if (!contactId) {
        return false;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return false;
      }

      // Get contact details
      const contactResult = await this.getById(
        contactId,
        'created_by_user_id, is_private'
      );

      if (!contactResult.success) {
        return false;
      }

      const contact = contactResult.data;

      // If not private, anyone can view
      if (!contact.is_private) {
        return true;
      }

      // Get user profile to check role
      const { data: profile } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();

      // Admins can view all
      if (profile?.role === 'Admin') {
        return true;
      }

      // Creators can view their own contacts
      if (contact.created_by_user_id === user.id) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking contact viewing permissions:', error);
      return false;
    }
  }
}

export const contactsService = new ContactsService();